import Avo from '@susu/custom-made/Avo';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import type { Monogram, MonogramPositions, Option } from '@susu/custom-made/utils/interfaces';
import { MONOGRAM_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { useEffect, useState } from 'react';
import { useI18N } from '@susu/i18n/client';

import { ButtonType } from '../button/button';
import Modal from '../modal/modal';
import { MonogramDefaultPanel } from './monogramDefaultPanel/monogramDefaultPanel';
import { MonogramDetailsPanel } from './monogramDetailsPanel/monogramDetailsPanel';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { useAvo } from '@susu/custom-made/hooks/useAvo';
import { getConfigurationTypeBySection } from '@susu/custom-made/utils/navigation';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const { ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

export const MonogramPanel = ({
  isMonogramShown,
  options,
  configurationTypeId,
}: {
  isMonogramShown: boolean;
  options: Option[];
  configurationTypeId: string;
}) => {
  const [isMonogramDetailsShown, setIsMonogramDetailsShown] = useState<boolean>(false);
  const [monogramData, setMonogramData] = useState<Option>();
  const [monogramObj, setMonogramObj] = useState<Monogram>({});
  const [selectedMonogramId, setSelectedMonogramId] = useState<number>(0);
  const [isDeleteMonogramModalOpen, setIsDeleteMonogramModalOpen] = useState<boolean>(false);
  const [deleteMonogramItem, setDeleteMonogramItem] = useState<MonogramPositions | null>(null);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const { removeSelectedMonogramFromMissingValues } = useMissingValues();
  const { section } = useNavigationContext();
  const { selectedMonogram, hiddenOptionsAndValues, addMonogram, deleteMonogram, selectedBaseStyle } =
    useOptionContext();
  const { getAvoStandardEventParams } = useAvo();
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);
  const { t } = useI18N();

  const applyMonogram = () => {
    addMonogram(monogramObj);

    if (configurationTypeId) {
      removeSelectedMonogramFromMissingValues(configurationTypeId, +Object.keys(monogramObj)[0]);
    }

    if (isAvoTrackingEnabled) {
      const selectedPositionId = Object.keys(monogramObj)[0];
      const selectedPositionName = monogramData?.positions?.[parseInt(selectedPositionId)]?.name;

      if (Object.keys(selectedMonogram).includes(selectedPositionId)) {
        Avo.cmEditConfirmed({
          ...getAvoStandardEventParams(`${section}_options`),
          cmItemType: Avo.CmItemType.OPTION,
          currentBaseStyle: selectedBaseStyle[configurationTypeId]?.name?.toLowerCase(),
          cmItemValue: `monogram_${selectedPositionName}`.toLowerCase(),
        });
      } else {
        Avo.cmCustomizationAdded({
          ...getAvoStandardEventParams(`${section}_options`),
          cmItemType: Avo.CmItemType.OPTION,
          currentBaseStyle: selectedBaseStyle[configurationTypeId]?.name?.toLowerCase(),
          cmItemValue: `monogram_${selectedPositionName}`.toLowerCase(),
        });
      }
    }
  };

  const deleteMonogramHandler = (position: MonogramPositions) => {
    setDeleteMonogramItem(position);
    setIsDeleteMonogramModalOpen(true);
    if (isAvoTrackingEnabled) {
      Avo.cmCustomizationDeleteInitiated({
        ...getAvoStandardEventParams(`${section}_options`),
        cmItemType: Avo.CmItemType.OPTION,
        currentBaseStyle: selectedBaseStyle[configurationTypeId]?.name?.toLowerCase(),
        cmItemValue: `monogram_${position.name}`.toLowerCase(),
      });
    }
  };

  const hideMonogramModal = () => {
    setDeleteMonogramItem(null);
    setIsDeleteMonogramModalOpen(false);
  };

  const confirmDeleteMonogram = () => {
    if (deleteMonogramItem) {
      if (isAvoTrackingEnabled) {
        Avo.cmCustomizationDeleteConfirmed({
          ...getAvoStandardEventParams(`${section}_options`),
          cmItemType: Avo.CmItemType.OPTION,
          currentBaseStyle: selectedBaseStyle[configurationTypeId]?.name?.toLowerCase(),
          cmItemValue: `monogram_${deleteMonogramItem.name}`.toLowerCase(),
        });
      }
      deleteMonogram(deleteMonogramItem.id);

      hideMonogramModal();

      removeSelectedMonogramFromMissingValues(configurationTypeId, deleteMonogramItem.id);
    }
  };

  const closeDeleteMonogramModal = () => {
    if (deleteMonogramItem) {
      if (isAvoTrackingEnabled) {
        Avo.cmCustomizationDeleteCancelled({
          ...getAvoStandardEventParams(`${section}_options`),
          cmItemType: Avo.CmItemType.OPTION,
          currentBaseStyle: selectedBaseStyle[configurationTypeId]?.name?.toLowerCase(),
          cmItemValue: `monogram_${deleteMonogramItem.name}`.toLowerCase(),
        });
      }
    }
    hideMonogramModal();
  };

  const editMonogram = (position: MonogramPositions) => {
    const tmpMonogramObj = {
      [position.id]: { ...selectedMonogram[position.id] },
    };
    setMonogramObj(tmpMonogramObj);
    setIsMonogramDetailsShown(true);
    setSelectedMonogramId(position.id);
    if (isAvoTrackingEnabled) {
      Avo.cmEditClicked({
        ...getAvoStandardEventParams(`${section}_options`),
        cmItemType: Avo.CmItemType.OPTION,
        currentBaseStyle: selectedBaseStyle[getConfigurationTypeBySection(section!)]?.name?.toLowerCase(),
        cmItemValue: `monogram_${position.name}`.toLowerCase(),
      });
    }
  };

  const openMonogramDetails = (positionId: number) => {
    const hiddenPositions = hiddenOptionsAndValues?.monogramPositions.length ?? 0;
    const selectedPositions = Object.keys(selectedMonogram).length;
    if (
      monogramData?.maxAllowedNumberOfPositions &&
      monogramData?.maxAllowedNumberOfPositions <= selectedPositions - hiddenPositions
    ) {
      setIsWarningModalOpen(true);
    } else {
      setIsMonogramDetailsShown(true);
      setSelectedMonogramId(positionId);
    }
  };

  const closeWarningModal = () => {
    setIsWarningModalOpen(false);
  };

  const allowedMonogramText = monogramData?.maxAllowedNumberOfPositions === 1 ?
    t('monogramPanel.maxAllowedOne') :
    t('monogramPanel.maxAllowed', {
      count: String(monogramData?.maxAllowedNumberOfPositions ?? 0),
    });

  useEffect(() => {
    if (options) {
      options.forEach(option => {
        if (option.type === 'MONOGRAM') {
          setMonogramData(option);
        }
      });
    }
  }, [options]);

  return (
    <>
      <MonogramDefaultPanel
        monogramData={monogramData}
        isMonogramShown={isMonogramShown}
        isDeleteMonogramModalOpen={isDeleteMonogramModalOpen}
        closeDeleteMonogramModal={closeDeleteMonogramModal}
        openMonogramDetails={openMonogramDetails}
        editMonogram={editMonogram}
        deleteMonogram={deleteMonogramHandler}
        confirmDeleteMonogram={confirmDeleteMonogram}
        deleteMonogramItem={deleteMonogramItem}
      />
      <MonogramDetailsPanel
        monogramData={monogramData}
        monogramObj={monogramObj}
        selectedMonogramId={selectedMonogramId}
        isMonogramDetailsShown={isMonogramShown && isMonogramDetailsShown}
        setIsMonogramDetailsShown={setIsMonogramDetailsShown}
        setMonogramObj={setMonogramObj}
        applyMonogram={applyMonogram}
      />
      <Modal
        isOpen={isWarningModalOpen}
        description={allowedMonogramText}
        firstButtonText={t('monogramPanel.cancel')}
        firstButtonType={ButtonType.SecondaryAlert}
        onFirstButtonClick={closeWarningModal}
        onOverlayClick={closeWarningModal}
        isFullSize={true}
        testId={MONOGRAM_TEST_IDS.ALLOWED_NUMBER_MODAL}
      />
    </>
  );
};
